import AutoModeIcon from '@mui/icons-material/AutoMode';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

export const FLOW_TRIGGER_MODE_DEFINITIONS = [
  {
    value: 'disabled',
    labelKey: 'workflow.automation_mode_disabled',
    labelFallback: 'Manual only',
    helpFallback: 'No automatic runs. Start this flow manually or from another flow.',
    tooltipFallback: 'No event, detail change, confirmation, interval, or calendar schedule starts this flow. Manual replay and the Trigger Flow action can still start it.'
  },
  {
    value: 'event',
    labelKey: 'workflow.automation_mode_event',
    labelFallback: 'New or updated event',
    helpFallback: 'Run when a document or event is processed.',
    tooltipFallback: 'Starts in the normal event-driven processing path when a document or event is processed. It does not wait for a detail-change filter or a schedule.'
  },
  {
    value: 'event_confirmed',
    labelKey: 'workflow.automation_mode_event_confirmed',
    labelFallback: 'When event is confirmed',
    helpFallback: 'Run only when an event changes from not confirmed to confirmed.',
    tooltipFallback: 'Starts only when an event transitions from not confirmed to confirmed. Unconfirming an event does not match this trigger.'
  },
  {
    value: 'event_change',
    labelKey: 'workflow.automation_mode_event_change',
    labelFallback: 'On event detail change',
    helpFallback: 'Run when an event detail matches the filters configured below.',
    tooltipFallback: 'Starts only when an explicit confirmation, tag, event date, or ingestion change matches every filter configured below. Empty filters accept any detailed change.'
  },
  {
    value: 'time',
    labelKey: 'workflow.automation_mode_time',
    labelFallback: 'Recurring interval',
    helpFallback: 'Run repeatedly every chosen number of minutes, hours, or days.',
    tooltipFallback: 'The scheduler starts this flow at the interval configured below. Event and document updates do not start it; actual start time follows the scheduler polling cycle.'
  },
  {
    value: 'calendar',
    labelKey: 'workflow.automation_mode_calendar',
    labelFallback: 'Calendar schedule',
    helpFallback: 'Run when the selected calendar fields match.',
    tooltipFallback: 'The scheduler starts this flow when all configured weekday, day-of-month, month, hour, and minute fields match in the saved time zone. Empty fields match any value.'
  }
];

export const normalizeFlowTriggerMode = (rawMode, fallbackAutoRunEnabled = null) => {
  const normalized = String(rawMode || '').trim().toLowerCase();
  if (['time', 'schedule', 'scheduled', 'auto'].includes(normalized)) return 'time';
  if (['calendar', 'cron', 'weekly', 'week', 'weekly schedule', 'weekly_schedule', 'weekly-schedule'].includes(normalized)) return 'calendar';
  if (['event', 'events', 'on_event', 'on event', 'change'].includes(normalized)) return 'event';
  if (['event_change', 'event change', 'event-change', 'timeevent_change', 'timeevent change', 'timeevent.changed'].includes(normalized)) return 'event_change';
  if (['event_confirmed', 'event confirmed', 'event-confirmed', 'confirmation', 'confirmed', 'on_confirm', 'on confirmation'].includes(normalized)) return 'event_confirmed';
  if (['disabled', 'disable', 'off', 'manual', 'none'].includes(normalized)) return 'disabled';
  return Number(fallbackAutoRunEnabled || 0) === 1 ? 'time' : 'event';
};

export const getTriggerModeOptions = (t) => FLOW_TRIGGER_MODE_DEFINITIONS.map((option) => ({
  ...option,
  label: t(option.labelKey, option.labelFallback),
  description: t(`workflow.automation_mode_help_${option.value}`, option.helpFallback),
  tooltip: t(`workflow.automation_mode_tooltip_${option.value}`, option.tooltipFallback),
}));

export const getFlowTriggerModeLabel = (rawMode, t, fallbackAutoRunEnabled = null) => {
  const normalizedMode = normalizeFlowTriggerMode(rawMode, fallbackAutoRunEnabled);
  const definition = FLOW_TRIGGER_MODE_DEFINITIONS.find((option) => option.value === normalizedMode)
    || FLOW_TRIGGER_MODE_DEFINITIONS[1];
  return t(definition.labelKey, definition.labelFallback);
};

const parseTriggerObject = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_error) {
    return {};
  }
};

const parseTriggerList = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === '') return [];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (_error) {
      return value.split(/[,;\s]+/);
    }
  }
  return [value];
};

const normalizedIntegerList = (value, minimum, maximum) => Array.from(new Set(
  parseTriggerList(value)
    .map(Number)
    .filter((item) => Number.isInteger(item) && item >= minimum && item <= maximum)
)).sort((left, right) => left - right);

const formatCalendarName = (value, type, locale) => {
  try {
    const date = type === 'weekday'
      ? new Date(Date.UTC(2024, 0, 1 + value))
      : new Date(Date.UTC(2024, value - 1, 1));
    return new Intl.DateTimeFormat(locale || undefined, {
      [type]: 'short',
      timeZone: 'UTC',
    }).format(date);
  } catch (_error) {
    return String(value);
  }
};

const formatLabeledCondition = (label, value) => `${label}: ${value}`;

const EVENT_CHANGE_TYPE_FALLBACKS = {
  any: 'Any change',
  confirm: 'Confirmation',
  tag: 'Tag',
  date: 'Event date',
  ingestion: 'Ingestion',
};

const EVENT_CHANGE_OPERATION_FALLBACKS = {
  any: 'Any operation',
  created: 'Created',
  updated: 'Updated',
  deleted: 'Deleted',
  processed: 'Processed',
};

export const getFlowTriggerConditionSummary = (flow, t, locale = undefined) => {
  const triggerMode = normalizeFlowTriggerMode(
    flow?.trigger_mode,
    flow?.auto_run_enabled
  );

  if (triggerMode === 'time') {
    const rawFrequency = Number(flow?.auto_run_frequency_value);
    const frequency = Number.isInteger(rawFrequency) && rawFrequency > 0 ? rawFrequency : 1;
    const rawUnit = String(flow?.auto_run_frequency_unit || '').trim().toLowerCase();
    const unit = ['minutes', 'hours', 'days'].includes(rawUnit) ? rawUnit : 'minutes';
    return `(${[
      formatLabeledCondition(t('workflow.automation_frequency_value_label', 'Every'), frequency),
      formatLabeledCondition(
        t('workflow.automation_frequency_unit_label', 'Unit'),
        t(`workflow.automation_unit_${unit}`, unit)
      ),
    ].join(', ')})`;
  }

  if (triggerMode === 'event_change') {
    const filter = parseTriggerObject(flow?.trigger_filter);
    const changeType = ['any', 'confirm', 'tag', 'date', 'ingestion'].includes(filter.change_type)
      ? filter.change_type
      : 'any';
    const operation = ['any', 'created', 'updated', 'deleted', 'processed'].includes(filter.operation)
      ? filter.operation
      : 'any';
    const conditions = [
      formatLabeledCondition(
        t('workflow.automation_event_change_type_label', 'Change type'),
        t(`workflow.automation_event_change_type_${changeType}`, EVENT_CHANGE_TYPE_FALLBACKS[changeType])
      ),
      formatLabeledCondition(
        t('workflow.automation_event_change_operation_label', 'Operation'),
        t(
          `workflow.automation_event_change_operation_${operation}`,
          EVENT_CHANGE_OPERATION_FALLBACKS[operation]
        )
      ),
    ];
    if (changeType === 'tag' && String(filter.tag_variable || '').trim()) {
      conditions.push(formatLabeledCondition(
        t('workflow.automation_event_change_tag_variable_label', 'Tag variable'),
        String(filter.tag_variable).trim()
      ));
    }
    if (changeType === 'tag' && String(filter.value_type || '').trim()) {
      conditions.push(formatLabeledCondition(
        t('workflow.automation_event_change_value_type_label', 'Value type'),
        String(filter.value_type).trim()
      ));
    }
    return `(${conditions.join('; ')})`;
  }

  if (triggerMode === 'calendar') {
    const conditionDefinitions = [
      {
        label: t('workflow.automation_weekdays_label', 'Weekdays'),
        values: normalizedIntegerList(flow?.auto_run_schedule_day_of_week, 0, 6),
        format: (value) => formatCalendarName(value, 'weekday', locale),
      },
      {
        label: t('workflow.automation_days_of_month_label', 'Days of month'),
        values: normalizedIntegerList(flow?.auto_run_schedule_day_of_month, 1, 31),
        format: String,
      },
      {
        label: t('workflow.automation_months_label', 'Months'),
        values: normalizedIntegerList(flow?.auto_run_schedule_month, 1, 12),
        format: (value) => formatCalendarName(value, 'month', locale),
      },
      {
        label: t('workflow.automation_hours_label', 'Hours'),
        values: normalizedIntegerList(flow?.auto_run_schedule_hour, 0, 23),
        format: (value) => String(value).padStart(2, '0'),
      },
      {
        label: t('workflow.automation_minutes_label', 'Minutes'),
        values: normalizedIntegerList(flow?.auto_run_schedule_minute, 0, 59),
        format: (value) => String(value).padStart(2, '0'),
      },
    ];
    const conditions = conditionDefinitions
      .filter((definition) => definition.values.length > 0)
      .map((definition) => formatLabeledCondition(
        definition.label,
        definition.values.map(definition.format).join(', ')
      ));
    if (!conditions.length) {
      conditions.push(t('workflow.automation_calendar_every_minute', 'Every minute'));
    }
    const timezone = String(flow?.auto_run_schedule_timezone || 'UTC').trim() || 'UTC';
    conditions.push(formatLabeledCondition(
      t('workflow.automation_timezone_label', 'Time zone'),
      timezone
    ));
    return `(${conditions.join('; ')})`;
  }

  return '';
};

/**
 * Shared visual language for flow triggers in the workflow canvas and dialogs.
 */
export const getTriggerVisualConfig = (triggerMode) => {
  if (triggerMode === 'time') {
    return {
      icon: AutoModeIcon,
      iconColor: '#2e7d32',
      borderColor: 'rgba(46, 125, 50, 0.42)',
      background: 'linear-gradient(135deg, #ffffff 0%, #f1f8e9 100%)',
      iconBackground: 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(102, 187, 106, 0.1) 100%)',
      shadow: '0 6px 20px rgba(76, 175, 80, 0.22), inset 0 1px 2px rgba(255, 255, 255, 0.55)',
      hoverShadow: '0 10px 26px rgba(76, 175, 80, 0.3), 0 4px 10px rgba(76, 175, 80, 0.16)',
      labelColor: '#2e7d32',
      labelBackground: 'rgba(76, 175, 80, 0.1)',
      labelBorder: 'rgba(76, 175, 80, 0.26)'
    };
  }

  if (triggerMode === 'calendar') {
    return {
      icon: CalendarMonthIcon,
      iconColor: '#8a5b00',
      borderColor: 'rgba(138, 91, 0, 0.38)',
      background: 'linear-gradient(135deg, #ffffff 0%, #fff8e6 100%)',
      iconBackground: 'linear-gradient(135deg, rgba(255, 193, 7, 0.22) 0%, rgba(255, 224, 130, 0.12) 100%)',
      shadow: '0 6px 20px rgba(255, 193, 7, 0.18), inset 0 1px 2px rgba(255, 255, 255, 0.55)',
      hoverShadow: '0 10px 24px rgba(255, 193, 7, 0.26), 0 4px 10px rgba(255, 193, 7, 0.12)',
      labelColor: '#8a5b00',
      labelBackground: 'rgba(255, 193, 7, 0.12)',
      labelBorder: 'rgba(255, 193, 7, 0.26)'
    };
  }

  if (triggerMode === 'event_confirmed') {
    return {
      icon: CheckCircleOutlineIcon,
      iconColor: '#2e7d32',
      borderColor: 'rgba(46, 125, 50, 0.38)',
      background: 'linear-gradient(135deg, #ffffff 0%, #f3faf4 100%)',
      iconBackground: 'linear-gradient(135deg, rgba(46, 125, 50, 0.18) 0%, rgba(102, 187, 106, 0.09) 100%)',
      shadow: '0 6px 18px rgba(46, 125, 50, 0.18), inset 0 1px 2px rgba(255, 255, 255, 0.55)',
      hoverShadow: '0 10px 24px rgba(46, 125, 50, 0.26), 0 4px 10px rgba(46, 125, 50, 0.12)',
      labelColor: '#2e7d32',
      labelBackground: 'rgba(46, 125, 50, 0.09)',
      labelBorder: 'rgba(46, 125, 50, 0.24)'
    };
  }

  if (triggerMode === 'event_change') {
    return {
      icon: CheckCircleOutlineIcon,
      iconColor: '#6a1b9a',
      borderColor: 'rgba(106, 27, 154, 0.36)',
      background: 'linear-gradient(135deg, #ffffff 0%, #faf5ff 100%)',
      iconBackground: 'linear-gradient(135deg, rgba(156, 39, 176, 0.18) 0%, rgba(186, 104, 200, 0.09) 100%)',
      shadow: '0 6px 18px rgba(156, 39, 176, 0.18), inset 0 1px 2px rgba(255, 255, 255, 0.55)',
      hoverShadow: '0 10px 24px rgba(156, 39, 176, 0.26), 0 4px 10px rgba(156, 39, 176, 0.12)',
      labelColor: '#6a1b9a',
      labelBackground: 'rgba(156, 39, 176, 0.09)',
      labelBorder: 'rgba(156, 39, 176, 0.24)'
    };
  }

  if (triggerMode === 'disabled') {
    return {
      icon: PauseCircleOutlineIcon,
      iconColor: '#6d6d6d',
      borderColor: 'rgba(125, 125, 125, 0.35)',
      background: 'linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%)',
      iconBackground: 'linear-gradient(135deg, rgba(117, 117, 117, 0.18) 0%, rgba(158, 158, 158, 0.08) 100%)',
      shadow: '0 3px 12px rgba(117, 117, 117, 0.16), inset 0 1px 2px rgba(255, 255, 255, 0.55)',
      hoverShadow: '0 8px 18px rgba(117, 117, 117, 0.24), 0 3px 8px rgba(117, 117, 117, 0.14)',
      labelColor: '#616161',
      labelBackground: 'rgba(117, 117, 117, 0.1)',
      labelBorder: 'rgba(117, 117, 117, 0.25)'
    };
  }

  return {
    icon: PlayArrowIcon,
    iconColor: '#1565c0',
    borderColor: 'rgba(21, 101, 192, 0.4)',
    background: 'linear-gradient(135deg, #ffffff 0%, #f1f7ff 100%)',
    iconBackground: 'linear-gradient(135deg, rgba(33, 150, 243, 0.2) 0%, rgba(100, 181, 246, 0.1) 100%)',
    shadow: '0 6px 20px rgba(33, 150, 243, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.55)',
    hoverShadow: '0 10px 24px rgba(33, 150, 243, 0.3), 0 4px 10px rgba(33, 150, 243, 0.14)',
    labelColor: '#1565c0',
    labelBackground: 'rgba(33, 150, 243, 0.1)',
    labelBorder: 'rgba(33, 150, 243, 0.24)'
  };
};
